import { ZoomableChart } from '../molecules/charts/zoomable';
import { EmptyState } from '../molecules/empty-state/empty-state';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import {
  formatMetricValue,
  normalizeMetricSeries,
  type DisplayMetricUnit,
} from './metric-series';
import { MetricSeries } from '@/lib/api/observability';

export function MetricPanel(props: {
  title: string;
  description: string;
  series: MetricSeries[];
  kind?: 'bar' | 'line' | 'area';
  loading?: boolean;
  unavailable?: boolean;
  unit?: DisplayMetricUnit;
  emptyTitle?: string;
  emptyDescription?: string;
  onZoom?: (from: string, to: string) => void;
}) {
  const normalized = normalizeMetricSeries(props.series, props.unit);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{props.title}</CardTitle>
        <CardDescription>{props.description}</CardDescription>
      </CardHeader>
      <CardContent>
        {props.loading ? (
          <Skeleton className="h-[220px] w-full" />
        ) : props.unavailable ? (
          <div className="flex h-[220px] items-center justify-center">
            <EmptyState
              title="Telemetry unavailable"
              description="This metric cannot be evaluated until its data source recovers."
            />
          </div>
        ) : normalized.incompatible ? (
          <div className="flex h-[220px] items-center justify-center">
            <EmptyState
              title="Incompatible metric units"
              description="This panel cannot compare metrics with different units."
            />
          </div>
        ) : !normalized.hasValues ? (
          <div className="flex h-[220px] items-center justify-center">
            <EmptyState
              title={props.emptyTitle ?? 'No data in this range'}
              description={
                props.emptyDescription ??
                'Change the time range or wait for new telemetry.'
              }
            />
          </div>
        ) : (
          <ZoomableChart
            data={normalized.data}
            kind={props.kind ?? 'line'}
            zoom={props.onZoom}
            valueFormatter={(value) =>
              formatMetricValue(value, normalized.unit)
            }
            ariaLabel={`${props.title}. ${props.description}`}
            className="h-[220px] min-h-[220px]"
          />
        )}
      </CardContent>
    </Card>
  );
}
